// services/syncService.js
import appURL from '../../appURL.json';
import { transformInstitutionMapping, transformSyncAccounts, transformSyncCycle, transformSyncHolidays, transformSyncItems, transformSyncPresence, transformSyncReasons, transformSyncTerritories, transformSyncUnits } from '../../components/OpalSyncFactoryData/OpalSyncFactoryData';
import { clearAllLocalData, syncDataToLocal } from '../../localDB';
import { transformSessionData, transformSyncSession } from '../../components/OpalCoverageLayout/CoverageFactoryData/CoverageFactoryData';
import { Agent, Announcements, EvaluationTemplates, Client, File } from '../../visualizations/visualizations';


// 🔁 Generic paginated sync function
const fetchPaginatedSync = async ({ storageKey, url, resultKey, method = "POST", forced = false, group = false }) => {
    let page = Number(localStorage.getItem(`lastSyncedPage_${storageKey}`)) || 1;
    let hasNext = true;
    let accumulated = JSON.parse(localStorage.getItem(`syncedData_${storageKey}`) || "[]");

    const userGroupIDs = localStorage.getItem('usr_g');
    
    try {
        while (hasNext) {
            const token = localStorage.getItem("token");
            const devId = localStorage.getItem("devId");
            let fullUrl = `${url}/${page}`;

            if (forced || group) {
                const params = new URLSearchParams();
                if (forced) params.append('Forced', 'True');
                if (group && userGroupIDs) params.append('Groups', userGroupIDs);
                fullUrl += `?${params.toString()}`;
            }

            const response = await fetch(fullUrl, {
                method,
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                    "X-ETR-Device-ID": devId
                }
            });

            if (!response.ok) {
                localStorage.setItem(`lastSyncedPage_${storageKey}`, page);
                localStorage.setItem(`syncedData_${storageKey}`, JSON.stringify(accumulated));
                return { success: false, error: `Sync failed for ${storageKey} at page ${page}: ${response.statusText}` };
            }

            const result = await response.json();
            const pageData = result[resultKey];
            hasNext = result.has_next;

            accumulated = [...accumulated, ...pageData];
            page += 1;

            localStorage.setItem(`lastSyncedPage_${storageKey}`, page);
            localStorage.setItem(`syncedData_${storageKey}`, JSON.stringify(accumulated));
        }

        localStorage.removeItem(`lastSyncedPage_${storageKey}`);
        localStorage.removeItem(`syncedData_${storageKey}`);

        return { success: true, data: accumulated };
    } catch (error) {
        localStorage.setItem(`lastSyncedPage_${storageKey}`, page);
        localStorage.setItem(`syncedData_${storageKey}`, JSON.stringify(accumulated));
        return { success: false, error: error.message || "Unexpected sync error" };
    }
};

const fetchPaginatedAccountSync = async ({ storageKey, url, resultKey, method = "POST", forced = false }) => {
    let page = Number(localStorage.getItem(`lastSyncedPage_${storageKey}`)) || 1;
    let hasNext = true;
    let accumulated = JSON.parse(localStorage.getItem(`syncedData_${storageKey}`) || "[]");

    const userGroupIDs = localStorage.getItem('usr_g');
    
    try {
        while (hasNext) {
            const token = localStorage.getItem("token");
            const devId = localStorage.getItem("devId");
            let fullUrl = `${url}/${page}/${userGroupIDs}`;

            if (forced) {
                const params = new URLSearchParams();
                if (forced) params.append('Forced', 'True');
                fullUrl += `?${params.toString()}`;
            }

            const response = await fetch(fullUrl, {
                method,
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                    "X-ETR-Device-ID": devId
                }
            });

            if (!response.ok) {
                localStorage.setItem(`lastSyncedPage_${storageKey}`, page);
                localStorage.setItem(`syncedData_${storageKey}`, JSON.stringify(accumulated));
                return { success: false, error: `Sync failed for ${storageKey} at page ${page}: ${response.statusText}` };
            }

            const result = await response.json();
            const pageData = result[resultKey];
            hasNext = result.has_next;

            accumulated = [...accumulated, ...pageData];
            page += 1;

            localStorage.setItem(`lastSyncedPage_${storageKey}`, page);
            localStorage.setItem(`syncedData_${storageKey}`, JSON.stringify(accumulated));
        }

        localStorage.removeItem(`lastSyncedPage_${storageKey}`);
        localStorage.removeItem(`syncedData_${storageKey}`);

        return { success: true, data: accumulated };
    } catch (error) {
        localStorage.setItem(`lastSyncedPage_${storageKey}`, page);
        localStorage.setItem(`syncedData_${storageKey}`, JSON.stringify(accumulated));
        return { success: false, error: error.message || "Unexpected sync error" };
    }
};

const fetchSessionSync = async (sessionDates) => {
    let currentDate = null;
    let accumulated = JSON.parse(localStorage.getItem(`syncedData_session`) || "[]");
    try {
        const token = localStorage.getItem('token');
        const devId = localStorage.getItem('devId');

        const userGroupIDs = localStorage.getItem('usr_g');

        for (const sessionDate of sessionDates) {
            currentDate = sessionDate;

            const response = await fetch(`${appURL.API_Session}?d=${sessionDate}&g=${userGroupIDs}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'X-ETR-DeviceID': devId
                },
            });

            if (!response.ok) {
                localStorage.setItem(`lastSyncedPage_session`, sessionDate);
                localStorage.setItem(`syncedData_session`, JSON.stringify(accumulated));
                return { success: false, error: `Sync failed for session at date ${sessionDate}: ${response.statusText}` };
            }

            const result = await response.json();
            const cycleID = Number(result.cycle.id);
            const transformedWithDate = result.sessions.map(item => ({
                ...item,
                date: sessionDate,
                cycleID: cycleID
            }));

            accumulated = [...accumulated, ...transformedWithDate];

            localStorage.setItem(`lastSyncedPage_session`, sessionDate);
            localStorage.setItem(`syncedData_session`, JSON.stringify(accumulated));
        }

        localStorage.removeItem(`lastSyncedPage_session`);
        localStorage.removeItem(`syncedData_session`);


        return { success: true, data: accumulated };

    } catch (error) {
        localStorage.setItem(`lastSyncedPage_session`, currentDate);
        localStorage.setItem(`syncedData_session`, JSON.stringify(accumulated));
        return { success: false, error: error.message || "Unexpected sync error" };
    }
};

const fetchPresenceSync = async (forced = false) => {
    let accumulated = JSON.parse(localStorage.getItem(`syncedData_presence`) || "{}");

    try {
        const token = localStorage.getItem('token');
        const devId = localStorage.getItem('devId');

        const fullUrl = `${appURL.API_Sync}/operations/presence${forced ? '?Forced=true' : ''}`;

        const response = await fetch(fullUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                'X-ETR-Device-ID': devId
            },
        });

        if (!response.ok) {
            // Save what we have and exit
            localStorage.setItem(`syncedData_presence`, JSON.stringify(accumulated));
            return { success: false, error: `Sync failed for presence` };
        }

        const result = await response.json();

        localStorage.removeItem(`syncedData_presence`);

        return { success: true, data: result };

    } catch (error) {
        localStorage.setItem(`syncedData_presence`, JSON.stringify(accumulated));
        return { success: false, error: error.message || "Unexpected sync error" };
    }
};


const fetchSyncDone = async () => {
    try {
        const token = localStorage.getItem('token');
        const devId = localStorage.getItem('devId');


        const response = await fetch(`${appURL.API_Sync}/done`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                'X-ETR-Device-ID': devId
            },
        });

        if (!response.ok) {
            return { success: false };
        }

        return { success: true };

    } catch (error) {
        return { success: false, error: error.message || "Unexpected sync error" };
    }
};

// 🔗 Main sync method — only this is exported
export const SyncData = async (forced = false) => {
    // if (forced) await clearAllLocalData();

    try {

        const cycleID = localStorage.getItem("c_id");
        const p_days = Number(localStorage.getItem("p_days")) || 0;
        const svr_date = localStorage.getItem("srv_date");
        const results = {};

        // Accounts by type
        const accountTypes = ["customer", "doctor", "agent", "institution"];
        const allAccounts = [];
        const allMissedCallReasons = [];

        const presence = await fetchPresenceSync(forced);
        if (presence.success) {
            results["presence"] = presence
        }

        const sessionDates = [];
        if (svr_date && !isNaN(p_days)) {
            const baseDate = new Date(svr_date);
            for (let i = -p_days; i <= p_days; i++) {
                const newDate = new Date(baseDate);
                newDate.setDate(baseDate.getDate() + i);
                sessionDates.push(newDate.toLocaleDateString('en-CA'));
            }
        }

        const sessions = await fetchSessionSync(sessionDates);
        if (sessions.success) {
            results["sessions"] = sessions;
        }

        for (const type of accountTypes) {
            const result = await fetchPaginatedAccountSync({
                storageKey: type,
                url: `${appURL.API_Sync}/accounts/${type}`,
                resultKey: "accounts",
                method: "POST",
                forced: forced
            });

            if (!result.success) {
                // return { success: false, message: `Failed to fetch ${type} accounts.` };
                continue
            }

            results[type] = result;
            const accountsWithType = result.data.map(account => ({ ...account, type }));
            allAccounts.push(...accountsWithType);
        }

        for (const type of accountTypes) {
            const combinedKey = `${type}_reasons`;

            const result = await fetchPaginatedSync({
                storageKey: combinedKey,
                url: `${appURL.API_Sync}/coverage/reasons/missed/${type}`,
                resultKey: "reasons",
                method: "POST",
                forced: forced
            });

            if (!result.success) {
                // return { success: false, message: `Failed to fetch ${type} reasons.` };
                continue
            }

            const accountsWithType = result.data.map(account => ({ ...account, type }));
            allMissedCallReasons.push(...accountsWithType);
        }

        // Other fetches
        const otherSyncs = [
            { key: "channels", url: `${appURL.API_Sync}/accounts/channels`, resultKey: "channels" },
            { key: "classifications", url: `${appURL.API_Sync}/accounts/classifications`, resultKey: "classifications" },
            { key: "frequencies", url: `${appURL.API_Sync}/coverage/frequency`, resultKey: "frequencies" },
            { key: "best_days", url: `${appURL.API_Sync}/coverage/best-day`, resultKey: "best_days" },
            { key: "best_times", url: `${appURL.API_Sync}/coverage/best-time`, resultKey: "best_times" },
            { key: "items", url: `${appURL.API_Sync}/inventory/items`, resultKey: "items", group: true },
            { key: "units", url: `${appURL.API_Sync}/inventory/items/units`, resultKey: "units" },
            { key: "holidays", url: `${appURL.API_Sync}/holidays`, resultKey: "holidays" },
            { key: "reasons", url: `${appURL.API_Sync}/coverage/reasons/advanced`, resultKey: "reasons" },
            { key: "territories", url: `${appURL.API_Sync}/administration/mapping/territory/${cycleID}`, resultKey: "territories" },
            { key: "cycles", url: `${appURL.API_Sync}/administration/cycles`, resultKey: "cycles" }
        ];

        for (const sync of otherSyncs) {
            const result = await fetchPaginatedSync({
                storageKey: sync.key,
                url: sync.url,
                resultKey: sync.resultKey,
                forced: forced,
                group: sync.group
            });

            if (!result.success) {
                // return { success: false, message: `Failed to fetch ${sync.key}.` };
                results[sync.key] = [];
                continue
            }

            results[sync.key] = result;
        }

        const transformPresence = await transformSyncPresence(results["presence"]?.data);
        const transformSessions = await transformSyncSession(results["sessions"]?.data);
        const transformInstitution = await transformInstitutionMapping(results["sessions"]?.data);
        const transformTerritories = await transformSyncTerritories(results["territories"]?.data);
        const transformAccounts = await transformSyncAccounts(allAccounts, results);
        const transformItems = await transformSyncItems(results["items"]?.data || []);
        const transformUnits = await transformSyncUnits(results["units"]?.data);
        const transformReasons = await transformSyncReasons(results["reasons"]?.data);
        const transformMissedCallReasons = await transformSyncReasons(allMissedCallReasons);
        const transformHolidays = await transformSyncHolidays(results["holidays"]?.data);
        const transformCycle = await transformSyncCycle(results["cycles"]?.data);

        const syncResults = [
            await syncDataToLocal('Attendance', transformPresence),
            await syncDataToLocal('session', transformSessions),
            await syncDataToLocal('institution', transformInstitution),
            await syncDataToLocal('territories', transformTerritories),
            await syncDataToLocal('customer', transformAccounts),
            await syncDataToLocal('item', transformItems),
            await syncDataToLocal('unit', transformUnits),
            await syncDataToLocal('AdvancedCallReasons', transformReasons),
            await syncDataToLocal('MissedCallReasons', transformMissedCallReasons),
            await syncDataToLocal('holiday', transformHolidays),
            await syncDataToLocal('agent', Agent),
            await syncDataToLocal('EvaluationTemplates', EvaluationTemplates),
            await syncDataToLocal('Announcements', Announcements),
            await syncDataToLocal('Cycle', transformCycle),
            await syncDataToLocal('Client', Client)
        ];

        // Find the first failed sync
        const failedSync = syncResults.find(res => !res.success);

        if (failedSync) {
            return { success: false, message: failedSync.message || 'Failed to sync some data to local DB.' };
        }

        return { success: true };
    } catch (error) {
        return { success: false, message: error.message || 'An unknown error occurred during sync.' };
    }
};

export const SyncDone = async () => {
    await fetchSyncDone();
}